import axios from 'axios';
import { toSchema } from '@cap/smart-render-core';
import { HttpResponse } from './request';
import { catTransaction } from '../util/Cat';
import { logger } from '../util/logger';

export interface ISiteInfo {
  baseUri: string; //
  endpointType: 'PC' | 'M'; // PC or Mobile
  footBarUrl: string;
  languageType: string;
  mDefaultSiteFlag: boolean; // 是否是默认站点
  navigationBarUrl: string;
  pcDefaultSiteFlag: boolean;
  siteImgUrl: string;
  siteNo: string;
  siteTitle: string;
  subSiteName: string;
  subSiteNo: string;
}

export interface ISubSiteInfo {
  webPageContentUrl: string; // 当前子站点对应的json schema
  webPageImgUrl: string;
  webPageNo: string;
  webPageTitle: string;
  webURI: string;
}

export interface ISmartSiteContext {
  __smart: {
    siteMap: { [key: string]: ISiteInfo };
    mSiteMap: { [key: string]: ISiteInfo };
    subSiteMap: { [key: string]: ISubSiteInfo };
    mSubSiteMap: { [key: string]: ISubSiteInfo };
  };
}

const SITE_NO = 'ST211201000001805';

const SMART_LOCALE_MAP = {
  'zh-CN': 'zh',
  'zh-HK': 'hk',
  'en-US': 'en',
};

const SMART_URL_MAP = {
  development: 'https://csr.cap.test.sankuai.com/',
  test: 'https://csr.cap.test.sankuai.com/',
  staging: 'http://cap-supervise.sankuai.com/',
  production: 'http://cap-supervise.sankuai.com/',
};

const ENV = (process.env.NEXT_ENV || 'staging') as keyof typeof SMART_URL_MAP;

const baseURL = SMART_URL_MAP[ENV] || SMART_URL_MAP.staging;

/** 获取所有站点信息 */
async function getSiteList(type: 'PC' | 'M' = 'PC') {
  const subscrible = catTransaction('getServerSideProps', '/smart/view/subSite/r/list');
  try {
    const { data } = await axios.get<HttpResponse<ISiteInfo[]>>('/smart/view/subSite/r/list', {
      params: {
        siteNo: SITE_NO,
        endpointType: type,
      },
      baseURL,
    });
    subscrible();
    return data.data;
  } catch (error) {
    logger.error(error);
    subscrible(false); // 上报错误
    throw new Error(`请求失败: /smart/view/subSite/r/list`);
  }
}

/** 获取子站点列表 */
async function getSubSiteInfo(subSiteNo: string) {
  const subscrible = catTransaction('getServerSideProps', '/smart/view/subSite/r/list');
  try {
    const { data } = await axios.get<HttpResponse<ISubSiteInfo[]>>('/smart/view/webPage/r/listBySubSiteNo', {
      params: {
        subSiteNo,
      },
      baseURL,
    });
    subscrible();
    return data.data;
  } catch (error) {
    logger.error(error);
    subscrible(false); // 上报错误
    throw new Error(`请求失败: /smart/view/webPage/r/listBySubSiteNo`);
  }
}

/** 获取站点json schema */
async function getSiteJsonSchema(siteJsonUrl: string) {
  // const subscrible = catTransaction('getServerSideProps', siteJsonUrl);
  try {
    const data = await axios.get(siteJsonUrl).then((res: any) => {
      return toSchema(res.data as any);
    });
    // subscrible();
    return data;
  } catch (error) {
    logger.error(error);
    // subscrible(false); // 上报错误
    throw new Error(`请求失败: ${siteJsonUrl}`);
  }
}

/**
 * @description 初始化灵动站点数据
 */
async function initSmartSiteData(_locale: 'zh-CN' | 'zh-HK' | 'en-US'): Promise<ISmartSiteContext> {
  const siteMap: { [key: string]: ISiteInfo } = {};
  const mSiteMap: { [key: string]: ISiteInfo } = {};
  const subSiteMap: { [key: string]: ISubSiteInfo } = {};
  const mSubSiteMap: { [key: string]: ISubSiteInfo } = {};
  const locale = SMART_LOCALE_MAP[_locale] || 'zh';
  const subscrible = catTransaction('getCsrSiteInfo', 'initSmartSizeData');

  try {
    const data = await Promise.all([getSiteList('PC'), getSiteList('M')])
      .then(([siteList, mSiteList]) => {
        siteList?.forEach((item) => {
          siteMap[item.baseUri] = item;
        });
        mSiteList?.forEach((item) => {
          mSiteMap[item.baseUri] = item;
        });
        return [siteMap[locale], mSiteMap[locale]];
      })
      .then(([site, mSite]) => {
        return Promise.all([getSubSiteInfo(site?.subSiteNo), getSubSiteInfo(mSite.subSiteNo)]).then(
          ([subSites, mSubSites]) => {
            (subSites || []).forEach((subsite) => {
              subSiteMap[subsite.webURI] = subsite;
            });
            (mSubSites || []).forEach((subsite) => {
              mSubSiteMap[subsite.webURI] = subsite;
            });
          },
        );
      })
      .then(() => {
        return {
          __smart: {
            siteMap,
            mSiteMap,
            subSiteMap,
            mSubSiteMap,
          },
        };
      });
    subscrible();
    return data;
  } catch (error) {
    logger.error(error);
    subscrible(false);
    throw new Error(`初始化灵动页面失败: initSmartSiteData`);
  }
}

export { getSiteList, getSubSiteInfo, getSiteJsonSchema, initSmartSiteData };
